import React from 'react';
import isBlank from '../utilities/isBlank';
import isNonNegativeNumber from '../utilities/isNonNegativeNumber';
import currencySym from 'currency-symbol-map';
import _ from 'lodash';
import validate from 'validate.js';
import { defineMessages, FormattedMessage, FormattedNumber } from 'react-intl';

const intlMessages = defineMessages({
  labelPresence: {
    id: 'web_2.validate_create_item_form.label_presence',
    defaultMessage: 'Please give your add-on a name'
  },
  descriptionPresence: {
    id: 'web_2.validate_create_item_form.description_presence',
    defaultMessage: 'Please enter a description'
  },
  visibleOnListing: {
    id: 'web_2.validate_create_item_form.visible_on_listing',
    defaultMessage: 'Please make a selection'
  },
  unitCostPrice: {
    id: 'web_2.validate_create_item_form.unit_cost_price',
    defaultMessage: 'Please enter a price'
  },
  unitCostInvalid: {
    id: 'web_2.validate_create_item_form.unit_cost_invalid',
    defaultMessage: 'Invalid'
  },
  taxError: {
    id: 'web_2.validate_create_item_form.tax_error',
    defaultMessage: 'Please enter an amount'
  },
  feeError: {
    id: 'web_2.validate_create_item_form.fee_error',
    defaultMessage: 'Please enter an amount'
  }
});

const validateCreateItemForm = (values, props) => {
  const { currency = 'USD' } = props;
  const currencySymbol = currencySym(currency);
  const {
    category,
    subcategory,
    label,
    unit_cost,
    unit_cost_basis,
    visible_on_listing,
    description,
    taxes = [],
    fees = [],
    count = ''
  } = values;

  const errors = {
    category: validate.single(category, {
      presence: { allowEmpty: false }
    }),
    subcategory: validate.single(subcategory, {
      presence: { allowEmpty: false }
    }),
    label: validate.single(label, {
      presence: { allowEmpty: false, message: <FormattedMessage { ...intlMessages.labelPresence } /> },
      length: {
        minimum: 5,
        tooShort: <FormattedMessage id='inbox.validate_create_item_form.error_minimum' defaultMessage='Please enter at least 5 characters' />
      }
    }),
    description: validate.single(description, {
      presence: { allowEmpty: false, message: <FormattedMessage { ...intlMessages.descriptionPresence } /> },
      length: {
        minimum: 10,
        maximum: 2000,
        tooShort: <FormattedMessage id='inbox.validate_create_item_form.description_minimum' defaultMessage='Please enter at least 10 characters' />,
        tooLong: <FormattedMessage id='inbox.validate_create_item_form.description_maximum' defaultMessage='The description is too long (2000 character limit)' />
      }
    }),
    visible_on_listing: validate.single(visible_on_listing, {
      presence: { allowEmpty: false, message: <FormattedMessage { ...intlMessages.visibleOnListing } /> }
    }),
    unit_cost: 999999 < Number(unit_cost)
      ? <FormattedMessage id='inbox.validate_create_item_form.unit_error' defaultMessage='Enter a price less than {num}' values={{ num: <FormattedNumber value={999999} currency={currency} style='currency' /> }} />
      : isBlank(unit_cost, <FormattedMessage { ...intlMessages.unitCostPrice } />) || isNonNegativeNumber(unit_cost, <FormattedMessage { ...intlMessages.unitCostInvalid } />),
    unit_cost_basis: validate.single(unit_cost_basis, {
      presence: { allowEmpty: false }
    }),
    count: 9999 < Number(count)
      ? <FormattedMessage id='inbox.validate_create_item_form.count_error_less' defaultMessage='Enter a quantity less than {num}' values={{ num: <FormattedNumber value={9999} /> }} />
      : 0 > Number(count)
        ? <FormattedMessage id='inbox.validate_create_item_form.count_error_greater' defaultMessage='Enter a quantity greater than {num}' values={{ num: <FormattedNumber value={0} /> }} />
        : ''
  };

  const taxesArrayErrors = [];
  _.map(taxes, (tax, i) => {
    const taxErrors = {};
    if (!tax.label) {
      taxErrors.label = true;
    }
    if (!tax.amount) {
      taxErrors.amount = <FormattedMessage { ...intlMessages.taxError } />;
    }
    if (35 < Number(tax.amount)) {
      taxErrors.amount = <FormattedMessage id='web_2.validate_create_item_form.tax_amount_number_error' defaultMessage='Enter the % as digits only. Number must be less than 35.' />;
    }
    if (4 < i) {
      taxErrors.amount = <FormattedMessage id='inbox.validate_create_item_form.tax_error_maximum' defaultMessage='Maximum of 5 taxes' />;
    }
    taxesArrayErrors[i] = taxErrors;
    errors.taxes = taxesArrayErrors;
  });

  const feesArrayErrors = [];
  _.map(fees, (fee, i) => {
    const feeErrors = {};
    if (!fee.label) {
      feeErrors.label = true;
    }
    if (!fee.amount) {
      feeErrors.amount = <FormattedMessage { ...intlMessages.feeError } />;
    }
    if (50 < Number(fee.amount) && '%' === fee.charge_unit) {
      feeErrors.amount = <FormattedMessage id='inbox.validate_create_item_form.fee_error_less_percent' defaultMessage='Enter a fee less than 50%' />;
    }
    if (99999 < Number(fee.amount) && '$' === fee.charge_unit) {
      feeErrors.amount = <FormattedMessage id='inbox.validate_create_item_form.fee_error_less_number' defaultMessage='Enter a fee less than {num}' values={{ num: <FormattedNumber value={99999} currency={currency} style='currency' /> }} />;
    }
    if (4 < i) {
      feeErrors.amount = <FormattedMessage id='inbox.validate_create_item_form.fee_error_maximum' defaultMessage='Maximum of 5 fees' />;
    }
    feesArrayErrors[i] = feeErrors;
    errors.fees = feesArrayErrors;
  });

  return errors;
};

export default validateCreateItemForm;
